import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import useNotification from '../../hooks/useNotification';
import _Icon from '../../components/AppIcon';
import _Button from '../../components/ui/Button';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import EmployerHeaderNav from '../../components/navigation/EmployerHeaderNav';
import { formatDistanceToNow } from 'date-fns';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { 
    notifications, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    fetchNotifications 
  } = useNotification();

  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const notificationsPerPage = 10;

  // Fetch notifications on mount
  useEffect(() => {
    if (currentUser?.id) {
      fetchNotifications();
    }
  }, [currentUser?.id, fetchNotifications]);

  // Filter notifications based on selected filter
  const getFilteredNotifications = () => {
    let filtered = [...notifications];
    
    if (filter === 'unread') {
      filtered = filtered.filter(notification => notification.unread);
    } else if (filter === 'high-priority') {
      filtered = filtered.filter(notification => notification.priority === 'high');
    } else if (filter === 'messages') {
      filtered = filtered.filter(notification => notification.type === 'message');
    } else if (filter === 'applications') {
      filtered = filtered.filter(notification => notification.type === 'application');
    } else if (filter === 'jobs') {
      filtered = filtered.filter(notification => notification.type === 'job');
    }
    
    // Sort notifications
    if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (sortBy === 'priority') {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      filtered.sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority]);
    }
    
    return filtered;
  };

  const filteredNotifications = getFilteredNotifications();
  
  // Calculate pagination
  const indexOfLastNotification = currentPage * notificationsPerPage;
  const indexOfFirstNotification = indexOfLastNotification - notificationsPerPage;
  const currentNotifications = filteredNotifications.slice(indexOfFirstNotification, indexOfLastNotification);
  const totalPages = Math.ceil(filteredNotifications.length / notificationsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (notification.unread) {
      markAsRead(notification.id);
    }
    
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  // Handle notification selection
  const handleSelectNotification = (notificationId) => {
    if (selectedNotifications.includes(notificationId)) {
      setSelectedNotifications(selectedNotifications.filter(id => id !== notificationId));
    } else {
      setSelectedNotifications([...selectedNotifications, notificationId]);
    }
  };

  // Handle bulk actions
  const handleBulkMarkAsRead = () => {
    selectedNotifications.forEach(id => markAsRead(id));
    setSelectedNotifications([]);
  };

  const handleBulkDelete = () => {
    selectedNotifications.forEach(id => deleteNotification(id));
    setSelectedNotifications([]);
  };

  // Handle clear all notifications
  const handleClearAllNotifications = () => {
    setPendingAction('clearAll');
    setShowConfirmModal(true);
  };

  const handleConfirmAction = () => {
    if (pendingAction === 'clearAll') {
      notifications.forEach(notification => deleteNotification(notification.id));
      setSelectedNotifications([]);
    }
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  const handleCancelAction = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
        return 'MessageSquare';
      case 'application':
        return 'FileText';
      case 'job':
        return 'Briefcase';
      case 'candidate':
        return 'Users';
      case 'system':
        return 'Bell';
      default:
        return 'Info';
    }
  };

  // Get notification color based on priority
  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-500';
      case 'medium':
        return 'text-yellow-500';
      case 'low':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <>
      {/* Navigation Header - Role-based */}
      {currentUser?.user_metadata?.role === 'va' || currentUser?.role === 'va' ? (
        <VAHeaderNav />
      ) : (
        <EmployerHeaderNav />
      )}
      
      <div className="notifications-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
              <h1 className="text-xl font-semibold text-gray-900">All Notifications</h1>
              
              <div className="flex flex-wrap items-center gap-2">
              {/* Filter dropdown */}
              <div className="relative">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread</option>
                  <option value="high-priority">High Priority</option>
                  <option value="messages">Messages</option>
                  <option value="applications">Applications</option>
                  <option value="jobs">Jobs</option>
                </select>
              </div>
              
              {/* Sort dropdown */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="priority">Priority</option>
                </select>
              </div>
              
              {/* Mark all as read button */}
              <_Button
                variant="secondary"
                size="sm"
                onClick={markAllAsRead}
                className="whitespace-nowrap"
              >
                Mark all as read
              </_Button>
              
              {/* Clear all notifications button */}
              <_Button
                variant="danger"
                size="sm"
                onClick={handleClearAllNotifications}
                className="whitespace-nowrap"
              >
                Clear all
              </_Button>
            </div>
          </div>
          
          {/* Bulk actions */}
          {selectedNotifications.length > 0 && (
            <div className="px-6 py-3 bg-gray-50 flex items-center justify-between">
              <span className="text-sm text-gray-700">
                {selectedNotifications.length} selected
              </span>
              <div className="flex space-x-2">
                <_Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBulkMarkAsRead}
                >
                  Mark as read
                </_Button>
                <_Button
                  variant="danger"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  Delete
                </_Button>
              </div>
            </div>
          )}
          
          {/* Notifications list */}
          <div className="divide-y divide-gray-200">
            {currentNotifications.length > 0 ? (
              currentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-6 py-4 flex items-start ${notification.unread ? 'bg-blue-50' : 'bg-white'} hover:bg-gray-50 duration-150`}
                >
                  {/* Checkbox */}
                  <div className="mr-4 pt-1">
                    <input
                      type="checkbox"
                      checked={selectedNotifications.includes(notification.id)}
                      onChange={() => handleSelectNotification(notification.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  {/* Icon */}
                  <div className={`flex-shrink-0 mr-4 ${getNotificationColor(notification.priority)}`}>
                    <_Icon name={getNotificationIcon(notification.type)} className="h-6 w-6" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0" onClick={() => handleNotificationClick(notification)}>
                    <div className="flex items-start justify-between">
                      <p className={`text-sm font-medium ${notification.unread ? 'text-gray-900' : 'text-gray-600'}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-500 whitespace-nowrap ml-2">
                        {notification.createdAt && !isNaN(new Date(notification.createdAt)) 
                          ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
                          : 'Recently'}
                      </p>
                    </div>
                    <p className={`mt-1 text-sm ${notification.unread ? 'text-gray-700' : 'text-gray-500'}`}>
                      {notification.message}
                    </p>
                    {notification.actionUrl && (
                      <div className="mt-2">
                        <_Button
                          variant="link"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(notification.actionUrl);
                          }}
                        >
                          View details
                        </_Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="ml-4 flex-shrink-0 flex">
                    {notification.unread && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                        }}
                        className="mr-2 text-gray-400 hover:text-gray-500"
                      >
                        <_Icon name="Check" className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <_Icon name="Trash2" className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-12 text-center">
                <_Icon name="Bell" className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No notifications</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filter !== 'all' 
                    ? `You don't have any ${filter} notifications.` 
                    : "You don't have any notifications yet."}
                </p>
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <_Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </_Button>
                <_Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </_Button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indexOfFirstNotification + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastNotification, filteredNotifications.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredNotifications.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <_Icon name="ChevronLeft" className="h-5 w-5" />
                    </button>
                    
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNumber
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <_Icon name="ChevronRight" className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 animate-in fade-in">
            <div className="flex items-center justify-center w-10 h-10 mx-auto bg-red-100 rounded-full">
              <_Icon name="AlertTriangle" className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900 text-center">
              Clear all notifications?
            </h3>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Are you sure you want to delete all notifications? This action cannot be undone.
            </p>
            <div className="mt-6 flex gap-3">
              <_Button
                variant="secondary"
                className="flex-1"
                onClick={handleCancelAction}
              >
                Cancel
              </_Button>
              <_Button
                variant="danger"
                className="flex-1"
                onClick={handleConfirmAction}
              >
                Delete all
              </_Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationsPage;


